import React, { useState, useEffect, useMemo, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { EditNewProdotto } from "./components/EditNewProdotto";
import { Chip, Avatar } from "@material-ui/core";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { Skeleton } from "@material-ui/lab";
import * as PropTypes from "prop-types";
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css'

import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  DataTypeProvider,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableFilterRow,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import SVG from "react-inlinesvg";
import { Card, Tooltip, OverlayTrigger, Button, Modal } from "react-bootstrap";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";

import { icons } from "../../config/config";
import * as apiProdotti from "../../api/prodotti";
import * as apiTarget from "../../api/target";
import Select from "react-select";
import { TrainOutlined } from "@material-ui/icons";

// Useful sub-components
const TableComponent = ({ ...restProps }) => (
  <Table.Table
    {...restProps}
    className="table table-hover table-head-custom table-vertical-center overflow-hidden"
  />
);

const RowComponent = ({ row, ...restProps }) => {
  let backgroundColor = ''; // Default

  if (parseInt(row.available, 10) === 0) {
    backgroundColor = 'rgba(255, 50, 50, 0.05)'; // Rosso grigio
  } else if (parseInt(row.available, 10) === 1) {
    backgroundColor = 'rgba(204, 255, 204, 0.4)'; // Rosso grigio
  }
  
  return (
    <Table.Row
      {...restProps}
      style={{
        backgroundColor,
        cursor: 'pointer',
      }}
    />
  );

};

const Toolbar = ({ addCallback, ...props }) => {
  return (
    <div {...props}>
      <Button onClick={addCallback} className="float-right">
        Aggiungi nuovo prodotto
      </Button>
    </div>
  );
};

// Filtro target multi-selezione
const TargetFilterCell = React.memo(({ filter, onFilter, targetOptions }) => {
  const handleChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      onFilter(null);
    } else {
      onFilter({
        value: selectedOptions.map((option) => option.value),
      });
    }
  };

  const selectedValues = useMemo(() => {
    if (!filter?.value || !Array.isArray(filter.value)) return [];
    return targetOptions.filter((option) =>
      filter.value.includes(option.value)
    );
  }, [filter?.value, targetOptions]);

  return (
    <th style={{ fontWeight: "normal", padding: "0.5rem" }}>
      <Select
        isMulti
        options={targetOptions}
        value={selectedValues}
        onChange={handleChange}
        placeholder="Seleziona target..."
        aria-label="Filtro per target"
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 30,
            fontSize: "0.85rem",
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={document.body}
      />
    </th>
  );
});

TargetFilterCell.propTypes = {
  filter: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.number),
  }),
  onFilter: PropTypes.func.isRequired,
  targetOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const FilterCell = React.memo(({ column, ...restProps }) => {
  if (column.name === "actions") {
    const ActionsFilterCell = ({ filter, onFilter }) => (
      <th style={{ fontWeight: "normal" }}>
        <input disabled style={{ visibility: "hidden" }} />
      </th>
    );
    ActionsFilterCell.propTypes = {
      filter: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      onFilter: PropTypes.func.isRequired,
    };
    ActionsFilterCell.defaultProps = {
      filter: null,
    };
    return <ActionsFilterCell {...restProps} />;
  }

  if (column.name === "targets") {
    return (
      <TargetFilterCell
        {...restProps}
        targetOptions={restProps.targetOptions}
      />
    );
  }

  return <TableFilterRow.Cell {...restProps} />;
});

FilterCell.propTypes = {
  column: PropTypes.shape({ name: PropTypes.string }).isRequired,
};

const DateFormattedColumn = (props) => (
  <DataTypeProvider
    formatterComponent={({ value }) => moment(value).format("DD/MM/YYYY")}
    {...props}
  />
);

const SkuFormattedColumn = (props) => (
  <DataTypeProvider formatterComponent={({ value }) => (
    <div style={{ textAlign: "center" }}>{value}</div>
  )}
  {...props}
  />
);

const QtaFormattedColumn = (props) => (
  <DataTypeProvider formatterComponent={({ value }) => (
    <div style={{ textAlign: "center" }}>{value}</div>
  )}
  {...props}
  />
);


const PriceFormattedColumn = (props) => (
  <DataTypeProvider
    formatterComponent={({ value }) =>
    <div style={{ textAlign: "center" }}>{(typeof value == "string" ? parseFloat(value) : value).toFixed(2)}€</div>
    }
    {...props}
  />
);

const IvaFormattedColumn = (props) => (
  <DataTypeProvider
    formatterComponent={({ value }) => (
      <div style={{ textAlign: "center" }}>
        {typeof value === "string" ? parseInt(value, 10) : value}
      </div>
    )}
    {...props}
  />
);

const AvailabilityFormattedColumn = (props) => (
  <DataTypeProvider
    formatterComponent={({ value }) => {
      const isAvailable = [1, "1", true].includes(value);
      return (
        <>
          {isAvailable ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Avatar
                style={{
                  width: 25,
                  height: 25,
                  backgroundColor: "var(--success)",
                }}
              >
                <span className="svg-icon svg-icon-md svg-icon-white">
                  <SVG src={toAbsoluteUrl(icons.ok)} />
                </span>
              </Avatar>
            </div>
          ) : null}
        </>
      );
    }}
    {...props}
  />
);

const ImageWithLightbox = ({ row, value }) => {
  const [isOpen, setIsOpen] = useState(false);
  const imageUrl = toAbsoluteUrl(row.image, row.division);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ alignSelf: 'center', marginRight: '5px' }}>
          {value}
        </div>
        <div
          onClick={() => setIsOpen(true)}
          style={{
            display: 'inline-block',
            borderRadius: '8px',
            height: '5vh',
            backgroundColor: 'white',
            textAlign: 'center',
            overflow: 'hidden',
            alignSelf: 'center',
            minWidth: '5vh',
            maxWidth: '5vh',
            minHeight: '5vh',
          }}
        >
          <img src={imageUrl} style={{ width:'100%', height: 'auto' }} alt="" />
        </div>
      </div>

      {isOpen && (
        <Lightbox
          mainSrc={imageUrl}
          onCloseRequest={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

const NameFormattedColumn = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <DataTypeProvider
      formatterComponent={({ row, value }) =>
        (row.image !== "" && row.image !== "0")
          ? <ImageWithLightbox row={row} value={value} />
          : <>{value}</>          
      }
      {...props}
    />
  );
}

const TargetFormatter = React.memo(({ value, targets = [] }) => {
  const targetNames = (value || [])
    .map((id) => targets.find((t) => t.id === id)?.name)
    .filter(Boolean)
    .join(", ");

  return targetNames || "Nessun target";
});

TargetFormatter.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

const TargetFormattedColumn = ({ for: columnNames, targets }) => (
  <DataTypeProvider
    for={columnNames}
    formatterComponent={({ value }) => (
      <TargetFormatter value={value} targets={targets} />
    )}
  />
);

TargetFormattedColumn.propTypes = {
  for: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const ActionsColumn = ({
  editCallback,
  availableCallback,
  notAvailableCallback,
  deleteCallback,
}) => (
  <DataTypeProvider
    for={["actions"]}
    formatterComponent={({ value }) => (
      <>
        <OverlayTrigger
          overlay={<Tooltip id="products-edit-tooltip">Modifica</Tooltip>}
        >
          {/*eslint-disable-next-line*/}
          <a
            className="btn btn-icon btn-light btn-hover-primary btn-sm mr-3"
            onClick={() => editCallback(value.id)}
          >
            <span className="svg-icon svg-icon-md svg-icon-primary">
              <SVG src={toAbsoluteUrl(icons.edit)} />
            </span>
          </a>
        </OverlayTrigger>
        <OverlayTrigger
          overlay={
            <Tooltip id="products-available-tooltip">
              {value.available
                ? "Imposta come non disponibile"
                : "Imposta come disponibile"}
            </Tooltip>
          }
        >
          {/*eslint-disable-next-line*/}
          <a
            className={
              "btn btn-icon btn-light btn-sm mr-3 " +
              (value.available ? "btn-hover-danger" : "btn-hover-success")
            }
            onClick={
              value.available
                ? () => notAvailableCallback(value.id)
                : () => availableCallback(value.id)
            }
          >
            <span
              className={
                "svg-icon svg-icon-md " +
                (value.available ? "svg-icon-danger" : "svg-icon-success")
              }
            >
              <SVG
                src={
                  value.available
                    ? toAbsoluteUrl(icons.close)
                    : toAbsoluteUrl(icons.ok)
                }
              />
            </span>
          </a>
        </OverlayTrigger>
        <OverlayTrigger
          overlay={<Tooltip id="products-delete-tooltip">Elimina</Tooltip>}
        >
          {/*eslint-disable-next-line*/}
          <a
            className="btn btn-icon btn-light btn-hover-danger btn-sm"
            onClick={() => deleteCallback(value.id)}
          >
            <span className="svg-icon svg-icon-md svg-icon-danger">
              <SVG src={toAbsoluteUrl(icons.delete)} />
            </span>
          </a>
        </OverlayTrigger>
      </>
    )}
  />
);

const AziendaColumn = (props) => {
  // console.log(JSON.stringify(props));
  let value = props.value;
  var vd = value.includes('Valderma');
  var rp = value.includes('Roypet');
  var rd = value.includes('Roydermal');
  
  return (
    <div className="pl-3">
      {vd ? (
        <Chip
          style={{ borderColor: "#003366", color: "#003366" }}
          variant="outlined"
          avatar={
            <Avatar
              alt="VD"
              src={toAbsoluteUrl("/media/logos/valderma-logo-bianco.png")}
              style={{ backgroundColor: "#003366", color: "#FFFFFF" }}
            >
              VD
            </Avatar>
          }
          label="Valderma"
        />
      ) : null}
      {rp ? (
        <Chip
          style={{ borderColor: "#313d85", color: "#313d85" }}
          variant="outlined"
          avatar={
            <Avatar
              alt="RP"
              src={toAbsoluteUrl("/media/products/roypet.png")}
            />
          }
          label="Roypet"
        />
      ) : null}
      {rd ? (
        <Chip
          style={{ borderColor: "#C92020", color: "#C92020" }}
          variant="outlined"
          avatar={
            <Avatar
              alt="RD"
              src={toAbsoluteUrl("/media/products/roydermal.png")}
            />
          }
          label="Roydermal"
        />
      ) : null}
    </div>
  );
};

export const AziendaFormattedColumn = (props) => (
  <DataTypeProvider formatterComponent={AziendaColumn} {...props} />
);

const DeleteModal = ({ name, show, hideCallback, deleteCallback }) => (
  <Modal show={show} onHide={hideCallback} className="card-custom gutter-b">
    <Modal.Header className="border-0 py-5 row">
      <Modal.Title>
        <h3 className="font-weight-bolder text-dark">Elimina prodotto</h3>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-4">
      Sei sicuro di voler eliminare definitivamente "{name}"
    </Modal.Body>
    <Modal.Footer className="border-0 py-5 row">
      <Button onClick={deleteCallback} variant="danger" className="float-right">
        Elimina
      </Button>
      <Button
        onClick={hideCallback}
        variant="secondary"
        className="float-right"
      >
        Annulla
      </Button>
    </Modal.Footer>
  </Modal>
);

const ConfirmAvailabilityModal = ({ productName, available, show, hideCallback, confirmCallback }) => (
  <Modal show={show} onHide={hideCallback} className="card-custom gutter-b">
    <Modal.Header className="border-0 py-5 row">
      <Modal.Title>
        <h3 className="font-weight-bolder text-dark">Cambia disponibilità</h3>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-4">
      Vuoi impostare "{productName}" come{" "}
      <strong>{available === 1 ? "Disponibile" : "Non Disponibile"}</strong>?
    </Modal.Body>
    <Modal.Footer className="border-0 py-5 row">
      <Button onClick={confirmCallback} variant="success" className="float-right">
        Conferma
      </Button>
      <Button
        onClick={hideCallback}
        variant="secondary"
        className="float-right"
      >
        Annulla
      </Button>
    </Modal.Footer>
  </Modal>
);

const columnExtensions = [
  { columnName: "sku", width: "80" },
  { columnName: "name", wordWrapEnabled: true },
  { columnName: "qta", wordWrapEnabled: true, width: 80 },
  { columnName: "available", width: "80" },
  { columnName: "iva", width: "80" },
  { columnName: "price", width: "80" },
  { columnName: "division", width: "150" },
  // { columnName: "dateCreated", width: "120", wordWrapEnabled: true },
  { columnName: "targets", width: "200" },
  { columnName: "actions", width: "150" },
];

// Messages
const tableMessages = {
  noData: "Nessun prodotto inserto",
};
const filterRowMessages = {
  filterPlaceholder: "Filtra...",
};
const pagingPanelMessages = {
  showAll: "Tutte",
  rowsPerPage: "Righe per pagina",
  info: "Da {from} a {to} ({count} richieste)",
};

// Paging and sorting
const pageSizes = [10, 20, 30, 0];
const initialPageSize = 10;
const initialSorting = [{ columnName: "id", direction: "desc" }];


  // User redux


export const TabellaProdotti = () => {

  const user = useSelector((state) => state.auth.user, shallowEqual);
  

  // Columns definition
  const columns = useMemo(
    () => [
      { name: "sku", title: "SKU" },
      { name: "name", title: "Nome" },
      { name: "qta", title: "Qta" },
      { name: "price", title: "Prezzo" },
      { name: "iva", title: "Iva" },
      { name: "available", title: "Disp." },
      { 
        name: "division", 
        title: "Azienda",  
        getCellValue: (row) => (parseInt(row.division,10)==11) ? ['Valderma'] : (row.division==1) ? ['Roypet'] : ['Roydermal'] || []
     },
      // { name: "dateCreated", title: "Creato il" },
      {
        name: "targets",
        title: "Target",
        getCellValue: (row) => row.targets || [],
      },
      { name: "actions", title: "Azioni" },
    ],
    []
  );

  // table data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [_internalUpdateCount, _setInternalUpdateCount] = useState(0);

  // edit page
  const [showEditPage, setShowEditPage] = useState(false);
  const [forEdit, setForEdit] = useState(false);
  // delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // availability confirm modal
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [pendingAvailability, setPendingAvailability] = useState(null);

  const [targets, setTargets] = useState([]);

  // Carica lista target
  const fetchTargets = useCallback(async () => {
    try {
      const data = await apiTarget.getAll(user.authToken);
      setTargets(data);
    } catch (error) {
      console.error("Errore nel caricamento dei target:", error);
    }
  }, [user.authToken]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Opzioni per react-select
  const targetOptions = useMemo(() => {
    return targets.map((target) => ({
      value: target.id,
      label: target.name,
    }));
  }, [targets]);

  const updateFromServer = () => {
    setLoading(true);
    apiProdotti
      .getAll(user.authToken)
      .then((data) =>
        setRows(
          data.map((row) => ({
            ...row,
            actions: { id: row.id, available: row.available },
          }))
        )
      )
      .then(() => setLoading(false));
  };
  const triggerServerUpdate = () =>
    _setInternalUpdateCount(_internalUpdateCount + 1);
  useEffect(() => {
    updateFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_internalUpdateCount]);

  const applyAvailability = useCallback(
    (id, value) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === id
            ? {
                ...row,
                available: value,
                actions: { ...row.actions, available: value },
              }
            : row
        )
      );

      apiProdotti
        .setAvailability(user.authToken, id, value)
        .catch((error) => {
          console.error(
            "Errore nell'aggiornamento della disponibilità:",
            error
          );
          triggerServerUpdate();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.authToken]
  );

  const confirmAvailability = useCallback(
    (id, value) => {
      setPendingAvailability({ id, value });
      setShowAvailabilityModal(true);
    },
    []
  );

  const getComparatorForType = useCallback((columnName) => {
    if (columnName === "targets") {
      return "targetIncludes";
    }
    return undefined;
  }, []);

  const customPredicates = useMemo(
    () => ({
      targetMatch: (value, filter) => {
        if (!filter?.value || !Array.isArray(filter.value)) return true;
        const rowTargets = Array.isArray(value) ? value : [];
        return rowTargets.some((targetId) => filter.value.includes(targetId));
      },
    }),
    []
  );

  // bind history to views to allow deep-linking
  const { location, push } = useHistory();
  useEffect(() => {
    if (!loading) {
      if (location.hash && location.hash === "#/new") {
        setForEdit(false);
        setShowEditPage(true);
        setShowDeleteModal(false);
      } else if (location.hash && location.hash.startsWith("#/delete/")) {
        const idForEdit = parseInt(location.hash.substring(9));
        if (!isNaN(idForEdit)) {
          const row = rows.find((r) => r.id == idForEdit);
          if (row !== undefined) {
            setForEdit(row);
            setShowEditPage(false);
            setShowDeleteModal(true);
          }
        }
      } else if (location.hash && location.hash.startsWith("#/edit/")) {
        const idForEdit = parseInt(location.hash.substring(7));
        if (!isNaN(idForEdit)) {
          const row = rows.find((r) => r.id == idForEdit);
          if (row !== undefined) {
            setForEdit(row);
            setShowEditPage(true);
            setShowDeleteModal(false);
          }
        }
      } else {
        setForEdit(false);
        setShowEditPage(false);
        setShowDeleteModal(false);
      }
    }
    /*eslint-disable-next-line*/
  }, [location.hash, loading]);

  // paging and sorting
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sorting, setSorting] = useState(initialSorting);

  if (!showEditPage) {
    return (
      <>
        <DeleteModal
          show={showDeleteModal}
          name={forEdit.name}
          hideCallback={() => {
            push("#/");
          }}
          deleteCallback={() => {
            apiProdotti.remove(user.authToken, forEdit.id).then(() => {
              push("#/");
              triggerServerUpdate();
            });
          }}
        />
        <ConfirmAvailabilityModal
          show={showAvailabilityModal}
          productName={
            pendingAvailability
              ? rows.find((row) => row.id === pendingAvailability.id)?.name
              : ""
          }
          available={pendingAvailability ? pendingAvailability.value : null}
          hideCallback={() => {
            setPendingAvailability(null);
            setShowAvailabilityModal(false);
          }}
          confirmCallback={() => {
            if (pendingAvailability) {
              applyAvailability(pendingAvailability.id, pendingAvailability.value);
            }
            setPendingAvailability(null);
            setShowAvailabilityModal(false);
          }}
        />
        <Card className="card-custom gutter-b">
          <Card.Header className="border-0 py-5 row">
            <Card.Title className="float-left col-auto">
              <h3 className="font-weight-bolder text-dark">Prodotti</h3>
            </Card.Title>
            <Toolbar
              addCallback={() => {
                push("#/new");
              }}
              className="float-right col-auto"
            />
          </Card.Header>
          <Card.Body className="pt-0">
            {!loading ? (
              <Grid key={targetOptions.length} rows={rows} columns={columns}>
                <NameFormattedColumn for={["name"]} />
                <QtaFormattedColumn for={["qta"]} />
                <SkuFormattedColumn for={["sku"]} />
                {/* <DateFormattedColumn for={["dateCreated"]} /> */}
                <PriceFormattedColumn for={["price"]} />
                <IvaFormattedColumn for={["iva"]} />
                <AvailabilityFormattedColumn for={["available"]} />
                <AziendaFormattedColumn for={["division"]} />
                <TargetFormattedColumn for={["targets"]} targets={targets} />
                <FilteringState />
                <IntegratedFiltering
                  columnExtensions={[
                    {
                      columnName: "targets",
                      predicate: customPredicates.targetMatch,
                    },
                  ]}
                  getComparatorForType={getComparatorForType}
                />
                <SortingState sorting={sorting} onSortingChange={setSorting} />
                <IntegratedSorting
                  getComparatorForType={getComparatorForType}
                />
                <PagingState
                  currentPage={currentPage}
                  onCurrentPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
                <IntegratedPaging />
                <ActionsColumn
                  editCallback={(id) => {
                    push(`#/edit/${id}`);
                  }}
                  availableCallback={(id) => {
                    confirmAvailability(id, 1);
                  }}
                  notAvailableCallback={(id) => {
                    confirmAvailability(id, 0);
                  }}
                  deleteCallback={(id) => {
                    push(`#/delete/${id}`);
                  }}
                />
                <Table
                  messages={tableMessages}
                  columnExtensions={columnExtensions}
                  tableComponent={TableComponent}
                  rowComponent={RowComponent}
                />
                <TableHeaderRow showSortingControls />
                <TableFilterRow
                  messages={filterRowMessages}
                  cellComponent={(props) => {
                    if (props.column.name === "targets") {
                      return (
                        <FilterCell {...props} targetOptions={targetOptions} />
                      );
                    }
                    return <FilterCell {...props} />;
                  }}
                />
                <PagingPanel
                  messages={pagingPanelMessages}
                  pageSizes={pageSizes}
                />
              </Grid>
            ) : (
              <div>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="rect" height={518} />
              </div>
            )}
          </Card.Body>
        </Card>
      </>
    );
  } else
    return (
      <EditNewProdotto
        prodotto={forEdit}
        hideEditPage={() => {
          push("#/");
        }}
        triggerServerUpdate={triggerServerUpdate}
      />
    );
};
